import { use } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { getTestProps } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import ContentObjectFormContext from '../../contexts/ContentObjectFormContext';
import UserContext from '../../contexts/UserContext';

import Switch from '../Switch/Switch';
import Tooltip from '../Tooltip/Tooltip';

interface OverrideSwitchProps {
  /**
   * Name of the field for which the override switch is rendered
   */
  name: string;
  /**
   * Should the tooltip about read only field be shown and switch disabled
   */
  readonly?: boolean;
  /**
   * Is switch checked
   */
  checked?: boolean;
  /**
   * Should the tooltip about unique field be shown and switch disabled when more than one object is edited
   */
  unique?: boolean;
  /**
   * Test id for the switch
   */
  testId?: string;
}

const OverrideSwitch = ({
  name = '',
  readonly = false,
  checked = false,
  unique = false,
  testId = '',
}: OverrideSwitchProps) => {
  const { contentTypeName } = useParams();
  const { t } = useTranslation();
  const { editedCount, setOverriddenFields } = use(ContentObjectFormContext);

  const { permissions } = use(UserContext);
  const canUpdate = permissions.canCo(
    contentTypeName,
    RolePermissions.PERMISSIONS_TYPES.UPDATE,
    name,
  );

  let tooltipText = null;
  if (unique && editedCount && editedCount > 1) {
    tooltipText = t('MassEdit.Unique');
  }
  if (readonly) {
    tooltipText = t('MassEdit.ReadOnly');
  }
  if (!canUpdate) {
    tooltipText = t('MassEdit.NoUpdatePermission');
  }
  return (
    <div className="flex justify-center items-start">
      <Tooltip
        tooltip={tooltipText}
        tooltipPlacement="rightCenter"
        phoneTooltipPlacement="rightCenter"
        additionalTooltipClasses={'mt-2 max-w-[140px]'}
      >
        <Switch
          label={t('Global.Override')}
          checked={checked}
          onChange={(_, checked) => setOverriddenFields?.(checked, name)}
          labelUp
          disabled={!!tooltipText}
          {...getTestProps(testId, 'override', 'testId')}
        />
      </Tooltip>
    </div>
  );
};

export default OverrideSwitch;
